import { z } from "zod";

import type { StepKey } from "@/src/lib/workflows/steps/types";
import { athenaCoverageResolutionAuditSchema } from "@/src/models/athena-coverage-resolution";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { referralAuthorizationDetailsAnswerSchema } from "@/src/models/referral-authorization-details";

export enum ApprovalType {
  ATHENA_COVERAGE = "athena_coverage",
  MANUAL_CONFIRMATION = "manual_confirmation",
  /** Asks ops whether to process a referral or send it back, given automation's eligibility check. */
  PATIENT_ELIGIBILITY = "patient_eligibility",
  REFERRAL_AUTH_DETAILS = "referral_auth_details",
  REFERRAL_AUTH_REQUIREMENT = "referral_auth_requirement",
  /** Asks ops to mark automation's result on an earlier step Correct or Incorrect before the run goes on. */
  STEP_VERDICT = "step_verdict",
  // Gates Pinnacle referral closure AND PA East referral-match upload. Both
  // ask ops "we matched this incoming doc to this eCW referral order, OK to
  // proceed with the downstream side-effect?". `closureInputs` inside the
  // metadata is still named for Pinnacle's downstream `referralClosureStep`
  // consumer (PA East populates it best-effort but doesn't read it).
  REFERRAL_MATCH = "referral_match",
  TPR_RECEIVING_ASSIGNMENT = "tpr_receiving_assignment",
  TPR_REFERRAL_OPS = "tpr_referral_ops",
}

export const referralMatchMetadataSchema = z.object({
  instruction: z.string().optional(),
  // CC workflow inputs are persisted for crash-safe idempotency on retries.
  closureInputs: z.object({
    patientMrn: z.string(),
    appointmentDate: z.string(),
    referralStartDate: z.string(),
    referralReasonCode: z.string(),
    assignedTo: z.string().nullable(),
    specialty: z.string().nullish(),
  }),
  evidence: z.object({
    progressNote: z.object({
      clinicName: z.string().nullable(),
      clinicAddress: z.string().nullable().optional(),
      providerName: z.string().nullable().optional(),
      reasonForVisit: z.string().nullable(),
      specialtyFolder: z.string().nullable(),
      dateOfEvent: z.string().nullable(),
      diagnosisCodes: z.array(z.string()),
    }),
    openReferrals: z.array(
      z.object({
        index: z.number(),
        performerName: z.string().nullable(),
        performerAddress: z.string().nullable().optional(),
        specialty: z.string().nullable(),
        reasonText: z.string().nullable(),
        icdCodes: z.array(z.string()),
        startDate: z.string().nullable(),
        endDate: z.string().nullable().optional(),
      })
    ),
  }),
  evaluation: z.object({
    matchedIndex: z.number(),
    reasoning: z.array(z.string()),
  }),
});

export type ReferralMatchMetadata = z.infer<typeof referralMatchMetadataSchema>;

const athenaCoverageReviewPolicySchema = z.object({
  insuranceId: z.string(),
  insurancePackageId: z.string().nullable(),
  planName: z.string().nullable(),
  sequence: z.number().int().positive().nullable(),
  subscriberId: z.string().nullable(),
  selfPay: z.boolean(),
  casePolicy: z.boolean(),
});

export const athenaCoverageConfirmationMetadataSchema = z.object({
  initialReview: z
    .object({
      reviewReasons: z.array(z.string()),
      currentCoverages: z.array(
        athenaCoverageReviewPolicySchema.extend({ ehrCancelledAt: z.string().nullable().optional() })
      ),
    })
    .optional(),
  resolution: athenaCoverageResolutionAuditSchema.optional(),
  instruction: z.string(),
  reviewMode: z.enum(["verify", "needs_action"]),
  departmentId: z.string(),
  expectedCoverageCount: z.number().int().positive(),
  expectSelfPay: z.boolean(),
  reviewReasons: z.array(z.string()).default([]),
  expectedCoverages: z.array(
    z.object({
      order: z.number().int().positive(),
      sequence: z.number().int().positive().nullable(),
      label: z.string(),
      insurancePackageId: z.string().nullable(),
      subscriberId: z.string().nullable(),
      casePolicy: z.boolean(),
    })
  ),
  currentCoverages: z.array(athenaCoverageReviewPolicySchema),
});

export type AthenaCoverageConfirmationMetadata = z.infer<typeof athenaCoverageConfirmationMetadataSchema>;

export const tprReceivingAssignmentConfirmationMetadataSchema = z.object({
  instruction: z.string(),
  providerName: z.string().nullable(),
  providerNpi: z.string().nullable(),
  targetDepartmentId: z.string().nullable(),
  reviewReasons: z.array(z.string()),
});

export type TprReceivingAssignmentConfirmationMetadata = z.infer<
  typeof tprReceivingAssignmentConfirmationMetadataSchema
>;

export const tprReferralOpsKindSchema = z.enum([
  "auth_request_upload",
  "authorization_review",
  "close_document",
  "manual_authorization",
  "pcp_referral_fax",
]);
export type TprReferralOpsKind = z.infer<typeof tprReferralOpsKindSchema>;

export const tprReferralOpsMetadataSchema = z.object({
  kind: tprReferralOpsKindSchema,
  instruction: z.string(),
  referralAuthId: z.string().nullable().optional(),
  write: z.enum(["paper", "authorization"]).nullable().optional(),
  pending: z.boolean().optional(),
  appointmentAttached: z.boolean().optional(),
  needsManualAppointmentAttach: z.boolean().optional(),
});
export type TprReferralOpsMetadata = z.infer<typeof tprReferralOpsMetadataSchema>;

/** What ops are asked when a patient-eligibility check holds the run; the check's result is the step's output. */
export const patientEligibilityApprovalMetadataSchema = z.object({ instruction: z.string() });
export type PatientEligibilityApprovalMetadata = z.infer<typeof patientEligibilityApprovalMetadataSchema>;

export const referralAuthRequirementApprovalMetadataSchema = z.object({ instruction: z.string() });
export type ReferralAuthRequirementApprovalMetadata = z.infer<typeof referralAuthRequirementApprovalMetadataSchema>;

export const stepVerdictApprovalMetadataSchema = z.object({ instruction: z.string() });
export type StepVerdictApprovalMetadata = z.infer<typeof stepVerdictApprovalMetadataSchema>;

export const referralAuthDetailsApprovalMetadataSchema = z.object({
  instruction: z.string(),
  round: z.number().int().positive(),
});
export type ReferralAuthDetailsApprovalMetadata = z.infer<typeof referralAuthDetailsApprovalMetadataSchema>;

export const manualConfirmationApprovalMetadataSchema = z.object({ instruction: z.string() });
export type ManualConfirmationApprovalMetadata = z.infer<typeof manualConfirmationApprovalMetadataSchema>;

export const decisionApprovalAnswerSchema = z.object({ proceed: z.boolean() }).strict();

export const approvalAnswerSchemas = {
  [ApprovalType.REFERRAL_AUTH_DETAILS]: referralAuthorizationDetailsAnswerSchema,
  [ApprovalType.REFERRAL_AUTH_REQUIREMENT]: decisionApprovalAnswerSchema,
  [ApprovalType.STEP_VERDICT]: decisionApprovalAnswerSchema,
} as const satisfies Partial<Record<ApprovalType, z.ZodType>>;

export const FORM_ANSWERED_APPROVAL_TYPES: ReadonlySet<ApprovalType> = new Set([ApprovalType.REFERRAL_AUTH_DETAILS]);

export type AnsweredApprovalType = keyof typeof approvalAnswerSchemas;
export type ApprovalAnswer<T extends AnsweredApprovalType> = z.infer<(typeof approvalAnswerSchemas)[T]>;

export function isAnsweredApprovalType(approvalType: ApprovalType): approvalType is AnsweredApprovalType {
  return Object.hasOwn(approvalAnswerSchemas, approvalType);
}

type BaseTaskApproval = DatabaseDefaults & {
  taskId: string;
  approved: boolean | null;
  decidedBy: string | null;
  answer: unknown;
  /**
   * The workflow step that raised this gate, as the V2 engine knew it at the time.
   *
   * This is what makes "may this replay supersede the gate?" answerable structurally: a gate may be
   * closed out exactly when its owning step is about to run again, because then the step re-raises
   * it if it still wants a human. Deriving the owner from {@link ApprovalType} instead cannot work —
   * `ATHENA_COVERAGE` is raised by both `CREATE_COVERAGE` and `CONFIRM_ATHENA_COVERAGE`.
   *
   * `null` on rows written before the column existed, and on any writer that doesn't set it. It
   * means *unknown*, never *none*: callers must fail closed rather than pick a plausible owner.
   */
  stepKey: StepKey | null;
};

export type TaskApproval =
  | (BaseTaskApproval & {
      approvalType: ApprovalType.REFERRAL_MATCH;
      metadata: ReferralMatchMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.ATHENA_COVERAGE;
      metadata: AthenaCoverageConfirmationMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.TPR_RECEIVING_ASSIGNMENT;
      metadata: TprReceivingAssignmentConfirmationMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.TPR_REFERRAL_OPS;
      metadata: TprReferralOpsMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.PATIENT_ELIGIBILITY;
      metadata: PatientEligibilityApprovalMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.REFERRAL_AUTH_REQUIREMENT;
      metadata: ReferralAuthRequirementApprovalMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.REFERRAL_AUTH_DETAILS;
      metadata: ReferralAuthDetailsApprovalMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.MANUAL_CONFIRMATION;
      metadata: ManualConfirmationApprovalMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.STEP_VERDICT;
      metadata: StepVerdictApprovalMetadata | null;
    });

export function parseTaskApprovalMetadata(approvalType: ApprovalType, metadata: unknown): TaskApproval["metadata"] {
  switch (approvalType) {
    case ApprovalType.REFERRAL_MATCH: {
      const parsed = referralMatchMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.ATHENA_COVERAGE: {
      const parsed = athenaCoverageConfirmationMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.TPR_RECEIVING_ASSIGNMENT: {
      const parsed = tprReceivingAssignmentConfirmationMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.TPR_REFERRAL_OPS: {
      const parsed = tprReferralOpsMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.PATIENT_ELIGIBILITY: {
      const parsed = patientEligibilityApprovalMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.REFERRAL_AUTH_REQUIREMENT: {
      const parsed = referralAuthRequirementApprovalMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.REFERRAL_AUTH_DETAILS: {
      const parsed = referralAuthDetailsApprovalMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.MANUAL_CONFIRMATION: {
      const parsed = manualConfirmationApprovalMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.STEP_VERDICT: {
      const parsed = stepVerdictApprovalMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    default: {
      const _exhaustive: never = approvalType;

      return _exhaustive;
    }
  }
}
